import { useId, useState } from "react";

import {
  classifyRunReadFailure,
  usePreviewRunEventQuery,
} from "@/api/previewInsights";
import { QueryPendingState } from "@/api/QueryPendingState";
import { queryKeys } from "@/api/hooks";
import { readPromptCallDetail } from "@/lib/previewPromptCall";
import { Button } from "@/ui/Button";
import { Notice } from "@/ui/Notice";
import { ExactPromptCall } from "@/ui/preview/ExactPromptCall";
import { PreviewRunReadFailure } from "@/ui/preview/PreviewRunReadFailure";
import { ErrorState } from "@/ui/states";

/**
 * The two prompts behind one changed pair, loaded ON DEMAND (#1107).
 *
 * The comparison (#1106) is BODY-FREE BY CONSTRUCTION: it is built from the two
 * reads that carry no prompt body and compares content by digest, so a prompt
 * body is never in the process at all. The contract says in as many words where
 * a byte-level view comes from instead — "to show a byte-level diff of one
 * aligned pair, read each side's event through the event-detail route" — and
 * that is exactly what this does: once per side, only when a clinician presses
 * for it, and only for the pair they pressed on.
 *
 * ON DEMAND IS THE POINT, not a refinement of it. Prompts are the largest thing
 * a trace holds, a comparison can name dozens of changed pairs, and the
 * event-detail read is the one the Editor Service audits as "full prompt detail
 * viewed". Loading them with the comparison would mean fetching every prompt of
 * two whole runs — and auditing a clinician as having read every one of them —
 * because they opened a tab.
 *
 * ## No difference is computed here
 *
 * The two prompts render SIDE BY SIDE, each exactly as its own run recorded it,
 * through the one renderer #1099 wrote. Nothing here aligns their lines, marks
 * their differing spans, or says which is better: WHAT differs is the server's
 * finding — the card above lists the fields it reported, and a prompt-provenance
 * pair lists the layers and segments it aligned — and this is the evidence
 * behind that finding, not a second opinion about it.
 *
 * Each side is read from ITS OWN run under its own locator, so a pair whose two
 * events carry the same reference (a producer that numbers events per run makes
 * that ordinary) is still two distinct reads of two distinct runs.
 */
export function ComparePromptBodies({
  projectId,
  baseRunRef,
  comparedRunRef,
  baseEventRef,
  comparedEventRef,
  coordinate,
}: {
  projectId: string;
  baseRunRef: string;
  comparedRunRef: string;
  baseEventRef: string;
  comparedEventRef: string;
  /** The pair's coordinate, so the control and the region say what they load. */
  coordinate: string;
}) {
  const [shown, setShown] = useState(false);
  const headingId = useId();

  if (!shown) {
    return (
      <div className="space-y-1">
        {/*
          THE COORDINATE IS IN THE ACCESSIBLE NAME. A comparison can hold many
          changed prompt pairs, and a screen reader's element list shows a
          button's name with none of the card around it — so a list of buttons
          all reading "Load both prompts" names nothing a clinician can choose
          between (#1128 review, finding 4). The visible text is contained in
          the accessible name, which is what WCAG 2.5.3 (Label in Name) asks.
        */}
        <Button
          variant="outline"
          size="sm"
          aria-label={`Load both prompts — ${coordinate}`}
          onClick={() => setShown(true)}
        >
          Load both prompts
        </Button>
        <p className="text-xs text-muted-foreground">
          The comparison carries no prompt text. Loading reads each run’s own
          event once, and each read is recorded as full prompt detail viewed.
        </p>
      </div>
    );
  }

  return (
    <section aria-labelledby={headingId} className="space-y-2">
      <h5 id={headingId} className="text-sm font-medium text-foreground">
        {`Recorded prompts — ${coordinate}`}
      </h5>
      {/*
        Two columns where there is room, stacked where there is not. Base first
        in both, so the reading order a screen reader and a narrow screen get is
        the same order the difference rows above use.
      */}
      <div className="grid gap-3 lg:grid-cols-2">
        <PromptSide
          side="Base run"
          projectId={projectId}
          previewRunRef={baseRunRef}
          eventRef={baseEventRef}
        />
        <PromptSide
          side="Compared run"
          projectId={projectId}
          previewRunRef={comparedRunRef}
          eventRef={comparedEventRef}
        />
      </div>
    </section>
  );
}

function PromptSide({
  side,
  projectId,
  previewRunRef,
  eventRef,
}: {
  side: string;
  projectId: string;
  previewRunRef: string;
  eventRef: string;
}) {
  const event = usePreviewRunEventQuery(projectId, previewRunRef, eventRef, {
    enabled: true,
  });
  const headingId = useId();

  return (
    <section
      aria-labelledby={headingId}
      className="min-w-0 space-y-2 rounded-md border border-border p-3"
    >
      <h6
        id={headingId}
        className="text-xs font-medium uppercase tracking-wide text-muted-foreground"
      >
        {side}
      </h6>
      <SideBody
        event={event}
        eventRef={eventRef}
        projectId={projectId}
        previewRunRef={previewRunRef}
      />
    </section>
  );
}

function SideBody({
  event,
  eventRef,
  projectId,
  previewRunRef,
}: {
  event: ReturnType<typeof usePreviewRunEventQuery>;
  eventRef: string;
  projectId: string;
  previewRunRef: string;
}) {
  if (event.isPending) {
    return (
      <QueryPendingState
        isPaused={event.isPaused}
        failureReason={event.failureReason}
        label="Loading the recorded prompt"
        errorTitle="This prompt could not be read"
        rows={2}
        queryKey={queryKeys.previewRunEvent(projectId, previewRunRef, eventRef)}
      />
    );
  }
  if (event.isError) {
    return (
      <PreviewRunReadFailure
        failure={classifyRunReadFailure(event.error)}
        error={event.error}
        onRetry={() => void event.refetch()}
      />
    );
  }

  const reading = readPromptCallDetail(event.data, eventRef);
  switch (reading.kind) {
    case "content_unavailable":
      // The pair was computed while this run's content was readable and the
      // run read says it is not now. A truthful sentence about the second
      // reading, never a prompt reconstructed from the first.
      return (
        <Notice tone="warning">
          This run no longer holds readable content for the event, so its prompt
          cannot be shown. That is not the same as a prompt that was never
          composed.
        </Notice>
      );
    case "mismatch":
      // FAIL CLOSED. A prompt shown under the wrong coordinate is worse than
      // no prompt: it would be read as this pair's evidence.
      return (
        <Notice tone="destructive" role="alert">
          The Editor Service answered with a different event than the one this
          pair names, so nothing is shown.
        </Notice>
      );
    case "unreadable":
      return (
        <ErrorState
          title="This prompt is not one this build can read"
          description={`The Editor Service relayed an event this build cannot place (at ${reading.at}). Nothing is shown rather than a partial reading.`}
        />
      );
    case "readable":
      return <ExactPromptCall call={reading.call} />;
  }
}
